import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import { TIMEFRAMES, SESSION_TF, baseChartOptions, DayLinePrimitive, buildDayLines, SessionBandPrimitive, buildSessionBands, SessionLegend, attachMousePan, attachBarCountdown } from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const POLL_MS = {
  '1m': 30000, '5m': 60000, '15m': 120000,
  '30m': 180000, '1h': 300000, '4h': 600000, '1D': 900000,
};

// Parse OCC symbol → human-readable parts.
// "SPXW260629C07520000" → { underlying:"SPXW", expiry:"06/29/26", type:"CALL", strike:7520 }
function parseContractSymbol(symbol) {
  if (!symbol) return null;
  const m = symbol.match(/^([A-Z.]+)(\d{6})([CP])(\d{8})$/);
  if (!m) return null;
  const [, underlying, yymmdd, cp, strikeRaw] = m;
  const yy = yymmdd.slice(0, 2), mm = yymmdd.slice(2, 4), dd = yymmdd.slice(4, 6);
  return {
    underlying,
    expiry:  `${mm}/${dd}/20${yy}`,
    type:    cp === 'C' ? 'CALL' : 'PUT',
    strike:  parseInt(strikeRaw, 10) / 1000,
  };
}

const CHART_HEIGHT = 350;

// How many seconds each timeframe bar covers — used to compute the current
// bar's start time for the synthetic live-price candle.
const TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1D': 86400,
};

export default function ContractChartWidget({ contract }) {
  const [tf, setTf] = useState('1m');

  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const seriesRef     = useRef(null);
  const primitiveRef  = useRef(null);
  const dayLineRef    = useRef(null);
  const pollRef       = useRef(null);
  const mountedRef   = useRef(true);
  const initialDone  = useRef(false);
  const liveBarRef   = useRef(null); // tracks current partial-minute bar

  // Countdown label reads the current tf through this ref (attached once on mount).
  const tfRef = useRef(tf);
  useEffect(() => { tfRef.current = tf; }, [tf]);

  // ── Create chart once on mount ─────────────────────────────────────────────
  // The container div is ALWAYS rendered (never display:none) so that
  // clientWidth is a real pixel value when createChart() is called.
  // A ResizeObserver handles any layout changes, including the widget going
  // from 0→full width on first paint in some browser/layout combinations.
  useEffect(() => {
    mountedRef.current = true;

    const opts = baseChartOptions(containerRef.current.clientWidth || 300);
    opts.height = CHART_HEIGHT;
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    series.attachPrimitive(dayLine);

    // ResizeObserver fires whenever the container's size changes — this
    // handles both window resize AND the first layout paint on desktop
    // where clientWidth may briefly be 0 during SSR / flex layout passes.
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const detachPan = attachMousePan(containerRef.current, chart);
    const detachCountdown = attachBarCountdown(containerRef.current, series, () => tfRef.current);

    return () => {
      mountedRef.current = false;
      detachPan();
      detachCountdown();
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = dayLineRef.current = null;
    };
  }, []);

  // Push the live contract price as a synthetic partial bar onto the chart.
  // Tracks O/H/L within the current bar interval so the candle is meaningful.
  // Safe to call before initialDone — returns early if chart isn't ready.
  const applyLivePrice = (price) => {
    if (!seriesRef.current || !price || !initialDone.current) return;
    const barSec = TF_SECONDS[tf] || 60;
    const nowSec = Math.floor(Date.now() / 1000);
    const barStart = Math.floor(nowSec / barSec) * barSec;
    const prev = liveBarRef.current;
    const bar = {
      time:  barStart,
      open:  (prev?.time === barStart) ? prev.open              : price,
      high:  (prev?.time === barStart) ? Math.max(prev.high, price) : price,
      low:   (prev?.time === barStart) ? Math.min(prev.low,  price) : price,
      close: price,
    };
    liveBarRef.current = bar;
    try { seriesRef.current.update(bar); } catch { /* chart not ready */ }
  };

  // ── Refetch when contract symbol or timeframe changes ─────────────────────
  useEffect(() => {
    if (!contract?.symbol) return;

    initialDone.current = false;
    liveBarRef.current = null;
    clearInterval(pollRef.current);

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(
          `${API_URL}/api/chart/contract-bars?symbol=${encodeURIComponent(contract.symbol)}&tf=${tf}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!mountedRef.current) return;

        if (isInitial || !initialDone.current) {
          seriesRef.current.setData(bars || []);
          if (bars && bars.length) {
            chartRef.current.timeScale().fitContent();
          }
          initialDone.current = true;
        } else if (bars && bars.length) {
          seriesRef.current.update(bars[bars.length - 1]);
        }

        // Day separator lines — drawn across all historical bars.
        // Session bands — only today's bars so pre-market doesn't appear twice
        // on multi-day charts (yesterday's data sits on the plain dark background).
        if (bars && bars.length) {
          dayLineRef.current?.setLines(buildDayLines(bars));
          if (SESSION_TF.has(tf)) {
            const todayET = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' });
            const todayBars = bars.filter(b =>
              new Date(b.time * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) === todayET
            );
            primitiveRef.current?.setBands(buildSessionBands(todayBars, { fitContent: true }));
          } else {
            primitiveRef.current?.clearBands();
          }
        }

        // Always re-apply the live price on top of the fetched bars so the
        // current partial bar is visible (bars API only returns completed bars).
        if (contract?.price) applyLivePrice(contract.price);
      } catch { /* keep stale view */ }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 30000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.symbol, tf]);

  // ── Update live bar whenever the widget's live price changes ──────────────
  useEffect(() => {
    if (contract?.price) applyLivePrice(contract.price);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.price]);

  const parsed = contract ? parseContractSymbol(contract.symbol) : null;
  const isCall  = parsed?.type === 'CALL';
  const typeColor = isCall ? '#22c55e' : '#ef4444';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">

      {/* Header */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {parsed ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-3 py-1.5 rounded border"
              style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
              {parsed.underlying}
            </span>
            <span className="text-xs font-semibold px-2 py-1 rounded"
              style={{ backgroundColor: typeColor + '22', color: typeColor }}>
              {parsed.type}
            </span>
            <span className="text-xs font-mono text-gray-300">
              ${parsed.strike % 1 === 0 ? parsed.strike.toFixed(0) : parsed.strike.toFixed(1)}
            </span>
            <span className="text-xs text-gray-500">{parsed.expiry}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-500">No contract selected</span>
        )}

        <div className="flex-1" />

        {/* Independent timeframe buttons */}
        <div className="flex items-center gap-1">
          {TIMEFRAMES.map(t => (
            <button
              key={t}
              onClick={() => setTf(t)}
              className="text-xs font-medium px-2 py-1 rounded transition-colors"
              style={
                t === tf
                  ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
                  : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
              }
            >{t}</button>
          ))}
        </div>

        {SESSION_TF.has(tf) && <SessionLegend />}

      </div>

      {/* Chart container — always in the DOM so clientWidth is always valid.
          A placeholder message is overlaid when no contract is selected.    */}
      <div className="relative" style={{ height: CHART_HEIGHT }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }} />
        {!contract?.symbol && (
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ color: '#374151', fontSize: 13, pointerEvents: 'none' }}>
            Select a contract below to view its chart
          </div>
        )}
      </div>
    </div>
  );
}
